import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/shared/cn';

/**
 * Red-tinted inline error strip — the standard "your save / install just
 * failed, here's why" affordance used by settings forms (ConnectorEditor,
 * SkillEditor, InstallForm). Renders a rounded card with the red-200 /
 * red-50 / red-700 palette already established across the app; dark
 * variants follow the same scheme. Same visual vocabulary as the
 * inline error part inside chat messages (`ErrorPartRenderer`), tuned for
 * form interiors instead of the chat surface.
 *
 * For chat-message error parts, use `ErrorPartRenderer` instead.
 */
export type InlineErrorProps = HTMLAttributes<HTMLDivElement>;

export const InlineError = forwardRef<HTMLDivElement, InlineErrorProps>(
  function InlineError({ className, role, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        role={role ?? 'alert'}
        className={cn(
          'rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
